import React, {Component} from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import {Field, change, untouch} from 'redux-form';
import {connect} from "react-redux";
import {compose} from "recompose";
import {RESOURCES as locationResources} from '../fuseLocationsResourceMap';
import {
    NumberInput,
    regex,
    minValue
} from 'admin-on-rest';
import VirtualizedSelectInput from '../components/VirtualizedSelectInput';
import FlatButton from 'material-ui/FlatButton';
import ContentAdd from 'material-ui/svg-icons/content/add';
import {Card, CardHeader, CardText} from 'material-ui/Card';
import {
    Table,
    TableHeader,
    TableHeaderColumn,
    TableRow,
    TableRowColumn,
    TableBody
} from "material-ui";
import LocationPriceTableRow from '../components/LocationPriceTableRow';
import FuseTheme from "../global/theme";

/**
 * A component for generating rows of LocationPriceTableRows.
 *
 */
class LocationPriceTable extends Component {
    constructor(props) {
        super(props);
        this.state = {
            price: {},
            locationPriceList: {},
            locationChoices: [],
            showCreateForm: false,
            locationIdValue: null,
            locationPriceValue: null,
        };
        this.handleCreateNewPriceItem = this.handleCreateNewPriceItem.bind(this);
        this.handleDeleteItem = this.handleDeleteItem.bind(this);
    }

    componentWillMount() {
        this.setState({
            locationPriceList: this.props.locationPriceList,
            price: this.props.price
        });
        this.fetchLocationData();
    }

    componentWillReceiveProps(nextProps) {
        if (
            nextProps.locationPriceList !== this.props.locationPriceList ||
            nextProps.price !== this.props.price
        ) {
            this.setState({
                locationPriceList: nextProps.locationPriceList,
                price: this.props.price
            });
        }
    }

    // Get Locations for adding a Location Price
    fetchLocationData() {
        const params = {
            filter: {},
            pagination: {page: 1, perPage: 10000},
            sort: {field: "id", order: "ASC"}
        };
        const resource = locationResources.LocationNames.GET_LIST(params);

        // Retrieve the data and assign results to the Select choices
        import('../shared/functions')
            .then(functions => {
                axios({
                    method: 'get',
                    url: resource.uri,
                    headers: functions.getGetHeaders()
                }).then((result) => {
                    if (result.data.payload) {
                        let choices = this.setChoiceKeys(result.data.payload);
                        this.setState({
                            locationChoices: choices
                        });
                    }
                }).catch((error) => {
                    if (error.response) {
                        // Server response outside 2xx
                        console.log(error.response.data);
                    } else if (error.request) {
                        // No response
                        console.log(error.request);
                    } else {
                        console.log('Error', error.message);
                    }
                });
            });
    }

    // Add unique keys for Location + ID choices
    setChoiceKeys = (choices) => {
        for (let choice of choices) {
            choice.name = choice.id + ' - ' + choice.name;
        }
        return choices;
    };

    // Handle event to display/hide Add Price fields
    handleToggleClick = (e) => {
        this.setState({
            showCreateForm: true,
        });
    };

    // Create a new Price item based on field values
    handleCreateNewPriceItem = (e) => {

        let locationId = this.props.locationIdValue;
        if (locationId.indexOf('location_') === -1) {
            locationId = 'location_' + locationId;
        }
        const locationPrice = this.props.locationPriceValue;
        if (!this.props.locationIdValue || !locationPrice) {
            return;
        }

        // Set the component list and the record
        let locationPriceList = this.props.locationPriceList;
        locationPriceList.set(locationId, locationPrice.toString());

        let priceList = JSON.parse(JSON.stringify(this.props.price));
        priceList[locationId] = locationPrice.toString();

        this.setState({
            price: priceList,
            locationPriceList: locationPriceList,
            locationIdValue: null,
            locationPriceValue: null,
            showCreateForm: false,
        }, () => {
            this.props.dispatch(change('record-form', 'price.' + locationId, locationPrice.toString()));
            this.props.onAddUpdate({[locationId]: locationPrice.toString()});

            // Reset the fields
            this.props.dispatch(change('record-form', 'locationSelectField', ''));
            this.props.dispatch(untouch('record-form', 'locationSelectField'));
            this.props.dispatch(change('record-form', 'locationPriceField', ''));
            this.props.dispatch(untouch('record-form', 'locationPriceField'));
        });
    };

    // Handle event from item Delete icon
    handleDeleteItem = (key) => {
        // Set the component list and the record
        let locationPriceList = this.props.locationPriceList;
        let priceList = JSON.parse(JSON.stringify(this.props.price));

        // Remove the key if it exists
        locationPriceList.delete(key);
        priceList[key] = undefined;

        this.setState({
            price: priceList,
            locationPriceList: locationPriceList,
            locationIdValue: null,
            locationPriceValue: null,
            showCreateForm: (locationPriceList.size === 0),
        }, () => {
            this.props.dispatch(change('record-form', 'price.' + key, ''));
            this.props.onDeleteUpdate(key);
        });
    };

    // Create table rows of Location Price items
    makePriceTable = (list, showDelete, deleteClickFunc) => {
        if (!list || list.size === 0 || !list.size) {
            return (
                <TableRow>
                    <TableRowColumn>No Location Prices</TableRowColumn>
                    <TableRowColumn>&nbsp;</TableRowColumn>
                    <TableRowColumn>&nbsp;</TableRowColumn>
                </TableRow>
            )
        }
        const rows = function* () {
            for (const [key, value] of list) {
                let location_id = key.toString();
                if (location_id.indexOf('location_') === -1) {
                    location_id = 'location_' + location_id;
                }
                let priceValue = (value) ? value.toString() : '';
                if (priceValue) {
                    yield (
                        <LocationPriceTableRow
                            key={key.toString()}
                            location_id={location_id}
                            price={priceValue}
                            showDelete={showDelete}
                            onDeleteClick={deleteClickFunc}/>
                    )
                }
            }
        };
        return rows();
    };

    render() {
        const styles = {
            addBlock: {padding: '8px 0 0 16px'},
            alignBottom: {verticalAlign: 'bottom'},
            alignTop: {verticalAlign: 'top'},
            block: {display: 'block'},
            fieldDescription: {
                display: 'block',
                width: '100%',
                fontSize: '14px',
                color: FuseTheme.palette.lightTextColor,
                margin: '0 0 1em 0',
            },
            fieldSmall: {display: 'inline-block', maxWidth: 80},
            fieldCreate: {display: 'inline-block', maxWidth: 120, margin: '0 1em 0 0', verticalAlign: 'top'},
            fieldSelect: {display: 'inline-block', maxWidth: 200, margin: '0 1em 0 0', verticalAlign: 'top'},
            icon: {height: '40px', padding: '10px', overflow: 'inherit'},
            rowHeight: {height: '40px'},
            tableCard: {display: 'block', margin: '1em 0 0 0'},
        };
        return (
            <Card style={styles.tableCard}>
                <CardHeader title="Location Prices" actAsExpander={true} showExpandableButton={true}/>
                <CardText expandable={true}>
                    {this.props.showCreate &&
                    <span style={styles.fieldDescription}>Add or Remove Location Prices. These are Prices that
                    override the Default Price for specific Locations. You may add as many Location Price
                    overrides as needed.</span>
                    }
                    <Table selectable={false} multiSelectable={false}>
                        <TableHeader displaySelectAll={false} adjustForCheckbox={false}>
                            <TableRow>
                                <TableHeaderColumn>Location</TableHeaderColumn>
                                <TableHeaderColumn>Price</TableHeaderColumn>
                                <TableHeaderColumn>Remove</TableHeaderColumn>
                            </TableRow>
                        </TableHeader>
                        <TableBody displayRowCheckbox={false}>
                            {[...this.makePriceTable(this.props.locationPriceList, this.props.showDelete, this.handleDeleteItem)]}
                        </TableBody>
                    </Table>
                    {this.props.showCreate &&
                    <div style={styles.addBlock}>
                        {!this.state.showCreateForm &&
                        <FlatButton
                            primary
                            label="Add a Location Price"
                            icon={<ContentAdd/>}
                            onClick={(e) => this.handleToggleClick(e)}
                        />
                        }
                        {this.state.showCreateForm &&
                        <div>
                            <Field name="locationSelectField"
                                   component={VirtualizedSelectInput}
                                   label="Location"
                                   choices={this.state.locationChoices}
                                   defaultValue={this.state.locationIdValue}
                                   elStyle={styles.fieldSelect}
                                   allowEmpty
                                   options={{
                                       className: "inline",
                                       placeholder: "Location",
                                   }}
                            />
                            <Field name="locationPriceField"
                                   label="Price"
                                   component={NumberInput}
                                   step="any"
                                   validate={[regex(/^\d*(\.\d{0,2})?$/, 'Must be a valid price.'), minValue(0, 'Must be a positive amount.')]}
                                   elStyle={styles.fieldCreate}
                                   defaultValue={this.state.locationPriceValue}
                            />
                            <FlatButton
                                primary
                                label="Add"
                                icon={<ContentAdd/>}
                                onClick={(e) => this.handleCreateNewPriceItem(e)}
                                style={styles.alignBottom}
                            />
                        </div>
                        }
                    </div>
                    }
                </CardText>
            </Card>
        )
    }
}

const mapStateToProps = (state, ownProps) => {
    let form = state.form['record-form'];
    if (form && form.values) {
        const selectValue = (form.values['locationSelectField']) ? form.values['locationSelectField'].toString() : null;
        const priceValue = (form.values['locationPriceField']) ? form.values['locationPriceField'].toString() : null;
        return {
            price: ownProps.price,
            locationPriceList: ownProps.locationPriceList,
            locationIdValue: selectValue,
            locationPriceValue: priceValue
        }
    }
    return {
        locationIdValue: null,
        locationPriceValue: null
    }
};

LocationPriceTable.propTypes = {
    addField: PropTypes.bool,
    onAddUpdate: PropTypes.func,
    onDeleteUpdate: PropTypes.func,
    price: PropTypes.object,
    locationPriceList: PropTypes.object.isRequired,
    locationIdValue: PropTypes.string,
    locationPriceValue: PropTypes.string,
    showCreate: PropTypes.bool,
    showDelete: PropTypes.bool,
    showCreateForm: PropTypes.bool,
};

LocationPriceTable.defaultProps = {
    addField: true,
    price: {},
    locationPriceList: new Map(),
    locationIdValue: null,
    locationPriceValue: null,
    showCreate: false,
    showDelete: false,
    showCreateForm: false,
};

const enhance = compose(
    connect(mapStateToProps)
);

export default enhance(LocationPriceTable);



// WEBPACK FOOTER //
// ./src/Fuse/components/LocationPriceTable.js